import { inject, Injectable, signal } from '@angular/core';
import { HighlightHandler } from '../handler/highlight.handler';
import { AIAPIService } from './ai.api';
import { ChatMessage } from './ai.models';

@Injectable({ providedIn: 'root' })
export class AIStore {
  private readonly api = inject(AIAPIService);
  private readonly highlightHandler = inject(HighlightHandler);

  public readonly messages = signal<ChatMessage[]>([]);
  public readonly isLoading = signal(false);

  public sendMessage(question: string): void {
    if (!question.trim() || this.isLoading()) return;

    this.highlightHandler.resetHighlight();
    this.messages.update((msgs) => [...msgs, { role: 'user', text: question }]);
    this.isLoading.set(true);

    const loadingIndex = this.messages().length;
    this.messages.update((msgs) => [...msgs, { role: 'assistant', text: '', loading: true }]);

    this.api.sendQuestion(question).subscribe({
      next: (res) => {
        const answer = res.answer ?? '';
        this.messages.update((msgs) =>
          msgs.map((m, i) =>
            i === loadingIndex ? { role: 'assistant', text: answer, loading: false } : m,
          ),
        );
        if (res?.highlight) {
          this.highlightHandler.setHighlight(res.highlight);
        } else {
          this.highlightHandler.resetHighlight();
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        const errText = `Error: ${err?.message ?? 'Could not reach the service.'}`;
        this.messages.update((msgs) =>
          msgs.map((m, i) =>
            i === loadingIndex ? { role: 'assistant', text: errText, loading: false } : m,
          ),
        );
        this.isLoading.set(false);
      },
    });
  }

  public clearMessages(): void {
    this.api.resetSession();
    this.messages.set([]);
  }
}
